import { fetchAuthSession } from 'aws-amplify/auth';
import { CognitoIdentityProviderClient, ListUsersCommand, AdminCreateUserCommand, AdminDeleteUserCommand, AdminAddUserToGroupCommand, AdminListGroupsForUserCommand, AdminUpdateUserAttributesCommand, AdminInitiateAuthCommand, AdminSetUserPasswordCommand } from '@aws-sdk/client-cognito-identity-provider';
import { cognitoConfig } from '../components/Constants';

const COGNITO_REGION = cognitoConfig.region;
const USER_POOL_ID = cognitoConfig.userPoolId;

// AWS SDK v3 Client oluştur
// Not: Frontend'den Cognito Admin API çağrısı yapmak için credentials gerekiyor
// Bu işlemler için backend API kullanılması önerilir, ancak test için frontend'den yapılabilir
const getCognitoClient = async () => {
  try {
    // Identity Pool ID kontrolü
    const identityPoolId = cognitoConfig.identityPoolId;
    if (!identityPoolId) {
      const errorMsg = 'Identity Pool ID tanımlı değil. Lütfen .env.local dosyasına REACT_APP_COGNITO_IDENTITY_POOL_ID ekleyin. Detaylar için COGNITO_IDENTITY_POOL_SETUP.md dosyasına bakın.';
      console.error('❌', errorMsg);
      throw new Error(errorMsg);
    }

    console.log('🔍 Identity Pool ID:', identityPoolId ? '✅ Tanımlı' : '❌ Tanımlı değil');
    
    const session = await fetchAuthSession({ forceRefresh: true });
    const credentials = session.credentials;
    
    if (!credentials) {
      const errorMsg = `AWS credentials bulunamadı. Identity Pool ID: ${identityPoolId ? 'Tanımlı' : 'Tanımlı değil'}. Lütfen Identity Pool kurulumunu tamamlayın.`;
      console.error('❌', errorMsg);
      console.log('💡 İpucu: Identity Pool ID doğru mu? IAM role yetkileri doğru mu?');
      throw new Error(errorMsg);
    }

    console.log('✅ AWS credentials alındı');
    return new CognitoIdentityProviderClient({
      region: COGNITO_REGION,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    });
  } catch (error) {
    console.error('❌ Cognito client oluşturma hatası:', error);
    throw error;
  }
};

// Backend API endpoint (opsiyonel - backend varsa kullanılır)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Cognito Admin API çağrıları için backend service
 * Şimdilik mock data döndürüyor, backend hazır olduğunda gerçek API'ye bağlanacak
 */
export const cognitoAdminService = {
  /**
   * Tüm kullanıcıları listele - Cognito'dan canlıdan çek
   */
  async listUsers() {
    try {
      // Backend API varsa önce onu dene (şimdilik backend'de bu endpoint yok, direkt Cognito kullan)
      // if (API_BASE_URL) {
      //   const session = await fetchAuthSession();
      //   const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
      //     method: 'GET',
      //     headers: {
      //       'Authorization': `Bearer ${session.tokens?.accessToken?.toString()}`,
      //       'Content-Type': 'application/json',
      //     },
      //   });

      //   if (response.ok) {
      //     const data = await response.json();
      //     return data.users || [];
      //   }
      // }

      // Backend yoksa AWS SDK ile direkt Cognito'dan çek
      if (!USER_POOL_ID) {
        throw new Error('Cognito User Pool ID tanımlı değil');
      }

      const client = await getCognitoClient();
      const allUsers = [];
      let paginationToken = undefined;

      do {
        const command = new ListUsersCommand({
          UserPoolId: USER_POOL_ID,
          PaginationToken: paginationToken,
          Limit: 60, // Maksimum 60 kullanıcı per request
        });

        const response = await client.send(command);
        
        if (response.Users) {
          // Her kullanıcı için groups bilgisini al
          const usersWithGroups = await Promise.all(
            response.Users.map(async (user) => {
              const email = user.Attributes?.find(attr => attr.Name === 'email')?.Value || '';
              const customRole = user.Attributes?.find(attr => attr.Name === 'custom:role')?.Value;
              
              // Groups bilgisini al
              let groups = [];
              try {
                const groupsCommand = new AdminListGroupsForUserCommand({
                  UserPoolId: USER_POOL_ID,
                  Username: user.Username,
                });
                const groupsResponse = await client.send(groupsCommand);
                groups = groupsResponse.Groups?.map(g => g.GroupName) || [];
              } catch (groupError) {
                console.warn(`Groups bilgisi alınamadı for ${user.Username}:`, groupError);
              }
              
              // Role öncelik sırası: custom:role > groups[0] > Tanımsız
              const role = customRole || groups[0] || 'Tanımsız';
              
              return {
                username: user.Username,
                email: email,
                role: role,
                groups: groups,
                status: user.UserStatus,
                enabled: user.Enabled,
                userCreateDate: user.UserCreateDate?.toISOString(),
                lastModifiedDate: user.UserLastModifiedDate?.toISOString(),
                attributes: user.Attributes || [],
              };
            })
          );
          
          allUsers.push(...usersWithGroups);
        }

        paginationToken = response.PaginationToken;
      } while (paginationToken);

      console.log('✅ Cognito\'dan', allUsers.length, 'kullanıcı çekildi');
      return allUsers;
    } catch (error) {
      console.error('❌ Kullanıcı listesi hatası:', error);
      
      // Hata durumunda kullanıcıya bilgi ver
      if (error.name === 'NotAuthorizedException' || error.message?.includes('credentials') || error.message?.includes('AWS credentials')) {
        throw new Error('Cognito Admin API erişimi için Identity Pool kurulumu gerekiyor. Lütfen COGNITO_IDENTITY_POOL_SETUP.md dosyasına bakın veya backend API kullanın.');
      }
      
      if (error.name === 'ResourceNotFoundException') {
        throw new Error('Cognito User Pool bulunamadı. Lütfen .env.local dosyasındaki REACT_APP_COGNITO_USER_POOL_ID değerini kontrol edin.');
      }
      
      throw error;
    }
  },

  /**
   * Yeni kullanıcı oluştur
   */
  async createUser(userData) {
    try {
      if (API_BASE_URL) {
        const session = await fetchAuthSession();
        const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.tokens?.accessToken?.toString()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Kullanıcı oluşturulamadı');
        }

        return await response.json();
      }

      // Backend yoksa AWS SDK ile direkt Cognito'da oluştur
      if (!USER_POOL_ID) {
        throw new Error('Cognito User Pool ID tanımlı değil. Lütfen .env.local dosyasına REACT_APP_COGNITO_USER_POOL_ID ekleyin.');
      }

      try {
        const client = await getCognitoClient();
        const command = new AdminCreateUserCommand({
          UserPoolId: USER_POOL_ID,
          Username: userData.username,
          UserAttributes: [
            { Name: 'email', Value: userData.email },
            { Name: 'email_verified', Value: 'true' },
          ],
          TemporaryPassword: userData.password,
          MessageAction: 'SUPPRESS', // Email gönderme
          ForceAliasCreation: false,
        });

        const response = await client.send(command);
        
        // Kullanıcıyı group'a ekle (role için)
        if (userData.role && response.User) {
          try {
            await this.addUserToGroup(userData.username, userData.role);
            console.log(`✅ Kullanıcı ${userData.username} ${userData.role} grubuna eklendi`);
          } catch (groupError) {
            console.warn('⚠️ Kullanıcı oluşturuldu ancak gruba eklenemedi:', groupError);
            // Kullanıcı oluşturuldu ama gruba eklenemedi - devam et
          }
        }

        return {
          username: response.User?.Username,
          email: userData.email,
          role: userData.role,
          status: response.User?.UserStatus,
          enabled: response.User?.Enabled,
        };
      } catch (error) {
        console.error('❌ Cognito kullanıcı oluşturma hatası:', error);
        
        // Daha açıklayıcı hata mesajları
        if (error.name === 'NotAuthorizedException' || error.message?.includes('credentials')) {
          throw new Error('Cognito Admin API erişimi için Identity Pool kurulumu gerekiyor. Lütfen COGNITO_IDENTITY_POOL_SETUP.md dosyasına bakın.');
        }
        
        if (error.name === 'UsernameExistsException') {
          throw new Error('Bu kullanıcı adı zaten kullanılıyor. Lütfen farklı bir kullanıcı adı seçin.');
        }
        
        if (error.name === 'InvalidParameterException') {
          throw new Error('Geçersiz parametre. Şifre en az 8 karakter olmalı ve belediye şifre politikasına uymalı.');
        }
        
        throw error;
      }
    } catch (error) {
      console.error('Kullanıcı oluşturma hatası:', error);
      throw error;
    }
  },

  /**
   * Kullanıcıyı sil
   */
  async deleteUser(username) {
    try {
      if (API_BASE_URL) {
        const session = await fetchAuthSession();
        const response = await fetch(`${API_BASE_URL}/api/admin/users/${username}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.tokens?.accessToken?.toString()}`,
          },
        });

        if (!response.ok) {
          throw new Error('Kullanıcı silinemedi');
        }

        return true;
      }

      // Backend yoksa AWS SDK ile direkt Cognito'da sil
      if (!USER_POOL_ID) {
        throw new Error('Cognito User Pool ID tanımlı değil');
      }

      try {
        const client = await getCognitoClient();
        const command = new AdminDeleteUserCommand({
          UserPoolId: USER_POOL_ID,
          Username: username,
        });

        await client.send(command);
        console.log(`✅ Kullanıcı ${username} silindi`);
        return true;
      } catch (error) {
        console.error('❌ Cognito kullanıcı silme hatası:', error);
        
        if (error.name === 'NotAuthorizedException' || error.message?.includes('credentials')) {
          throw new Error('Cognito Admin API erişimi için Identity Pool kurulumu gerekiyor.');
        }
        
        if (error.name === 'UserNotFoundException') {
          throw new Error('Kullanıcı bulunamadı.');
        }
        
        throw error;
      }
    } catch (error) {
      console.error('Kullanıcı silme hatası:', error);
      throw error;
    }
  },

  /**
   * Kullanıcıyı bir group'a ekle
   */
  async addUserToGroup(username, groupName) {
    try {
      if (API_BASE_URL) {
        const session = await fetchAuthSession();
        const response = await fetch(`${API_BASE_URL}/api/admin/users/${username}/groups`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.tokens?.accessToken?.toString()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ groupName }),
        });

        if (!response.ok) {
          throw new Error('Kullanıcı group\'a eklenemedi');
        }

        return true;
      }

      // Backend yoksa AWS SDK ile direkt Cognito'da group'a ekle
      if (!USER_POOL_ID) {
        throw new Error('Cognito User Pool ID tanımlı değil');
      }

      try {
        const client = await getCognitoClient();
        const command = new AdminAddUserToGroupCommand({
          UserPoolId: USER_POOL_ID,
          Username: username,
          GroupName: groupName,
        });

        await client.send(command);
        console.log(`✅ Kullanıcı ${username} ${groupName} grubuna eklendi`);
        return true;
      } catch (error) {
        console.error('❌ Cognito group ekleme hatası:', error);
        
        if (error.name === 'NotAuthorizedException' || error.message?.includes('credentials')) {
          throw new Error('Cognito Admin API erişimi için Identity Pool kurulumu gerekiyor.');
        }
        
        if (error.name === 'ResourceNotFoundException') {
          throw new Error(`Group '${groupName}' bulunamadı. Lütfen Cognito'da bu group'u oluşturun.`);
        }
        
        throw error;
      }
    } catch (error) {
      console.error('Group ekleme hatası:', error);
      throw error;
    }
  },

  /**
   * Kullanıcı attribute'larını güncelle (örn: bölgeler)
   */
  async updateUserAttributes(username, attributes) {
    try {
      // Backend API kontrolünü atla, direkt AWS SDK ile Cognito'da güncelle
      // Çünkü backend API endpoint'i henüz hazır değil veya yanlış yapılandırılmış
      if (!USER_POOL_ID) {
        throw new Error('Cognito User Pool ID tanımlı değil');
      }

      const client = await getCognitoClient();
      
      // Attributes array'ini Cognito formatına çevir
      const userAttributes = Object.entries(attributes).map(([name, value]) => {
        // Name'i düzelt: custom: ile başlamalı
        const attrName = name.startsWith('custom:') ? name : `custom:${name}`;
        
        // Value'yu string'e çevir (eğer object ise JSON.stringify)
        let attrValue;
        if (typeof value === 'object') {
          attrValue = JSON.stringify(value);
        } else {
          attrValue = String(value);
        }
        
        return {
          Name: attrName,
          Value: attrValue,
        };
      });

      console.log('📝 Güncellenecek attribute\'lar:', userAttributes);

      const command = new AdminUpdateUserAttributesCommand({
        UserPoolId: USER_POOL_ID,
        Username: username,
        UserAttributes: userAttributes,
      });

      await client.send(command);
      console.log(`✅ Kullanıcı ${username} attribute'ları güncellendi`);
      return true;
    } catch (error) {
      console.error('❌ Cognito attribute güncelleme hatası:', error);
      
      if (error.name === 'NotAuthorizedException' || error.message?.includes('credentials')) {
        throw new Error('Cognito Admin API erişimi için Identity Pool kurulumu gerekiyor.');
      }
      
      if (error.name === 'InvalidParameterException') {
        throw new Error('Geçersiz attribute parametresi. Lütfen attribute adlarını ve değerlerini kontrol edin.');
      }
      
      if (error.name === 'UserNotFoundException') {
        throw new Error('Kullanıcı bulunamadı.');
      }
      
      throw error;
    }
  },

  /**
   * Kullanıcının attribute'larını oku
   */
  async getUserAttributes(username) {
    try {
      const users = await this.listUsers();
      const user = users.find(u => u.username === username);
      
      if (!user) {
        throw new Error('Kullanıcı bulunamadı');
      }

      // Attributes'ı object'e çevir
      const attributes = {};
      if (user.attributes) {
        user.attributes.forEach(attr => {
          attributes[attr.Name] = attr.Value;
        });
      }

      return attributes;
    } catch (error) {
      console.error('Attribute okuma hatası:', error);
      throw error;
    }
  },

  /**
   * Admin olarak başka bir kullanıcıya giriş yap (token al)
   * Kullanıcının şifresini geçici olarak değiştirip giriş yapar, sonra eski haline geri döndürür
   * Not: Bu işlem güvenlik açısından dikkatli kullanılmalıdır
   */
  async loginAsUser(username) {
    try {
      if (!USER_POOL_ID) {
        throw new Error('Cognito User Pool ID tanımlı değil');
      }

      const client = await getCognitoClient();
      
      // Kullanıcının email'ini al
      const users = await this.listUsers();
      const targetUser = users.find(u => u.username === username);
      
      if (!targetUser) {
        throw new Error('Kullanıcı bulunamadı');
      }

      // Sabit şifre kullan (kullanıcıların şifresi her seferinde aynı değere sıfırlanır)
      // Bu şekilde kullanıcılar bir sonraki girişlerinde aynı şifreyi kullanabilir
      const tempPassword = 'Pro.123456';
      
      // Kullanıcının şifresini geçici olarak değiştir
      // Permanent: true yapıyoruz çünkü geçici şifre NEW_PASSWORD_REQUIRED challenge'ına neden olabilir
      const setPasswordCommand = new AdminSetUserPasswordCommand({
        UserPoolId: USER_POOL_ID,
        Username: username,
        Password: tempPassword,
        Permanent: true, // Kalıcı şifre (geçici şifre challenge'a neden olabilir)
      });

      await client.send(setPasswordCommand);
      
      // AdminInitiateAuth ile kullanıcının token'ını al
      const authCommand = new AdminInitiateAuthCommand({
        UserPoolId: USER_POOL_ID,
        ClientId: cognitoConfig.userPoolWebClientId,
        AuthFlow: 'ADMIN_NO_SRP_AUTH',
        AuthParameters: {
          USERNAME: username,
          PASSWORD: tempPassword,
        },
      });

      const response = await client.send(authCommand);
      
      console.log('AdminInitiateAuth response:', JSON.stringify(response, null, 2));
      
      if (response.AuthenticationResult) {
        return {
          username: targetUser.username,
          email: targetUser.email,
          accessToken: response.AuthenticationResult.AccessToken,
          idToken: response.AuthenticationResult.IdToken,
          refreshToken: response.AuthenticationResult.RefreshToken,
        };
      } else if (response.ChallengeName) {
        // Challenge varsa (örn: NEW_PASSWORD_REQUIRED)
        console.warn('Challenge alındı:', response.ChallengeName);
        throw new Error(`Giriş yapılamadı: Kullanıcı şifre değiştirme gerektiriyor (${response.ChallengeName}). Lütfen kullanıcının şifresini manuel olarak sıfırlayın.`);
      } else {
        console.error('Beklenmeyen response:', response);
        throw new Error(`Token alınamadı: Beklenmeyen yanıt. Response: ${JSON.stringify(response)}`);
      }
    } catch (error) {
      console.error('Kullanıcıya giriş yapma hatası:', error);
      
      // IAM yetki hatası için özel mesaj
      if (error.name === 'AccessDeniedException' || error.message?.includes('not authorized')) {
        throw new Error('IAM yetki hatası: Identity Pool role\'üne "cognito-idp:AdminSetUserPassword" ve "cognito-idp:AdminInitiateAuth" yetkileri eklenmelidir. Lütfen COGNITO_IDENTITY_POOL_SETUP.md dosyasına bakın.');
      }
      
      // Auth flow hatası için özel mesaj
      if (error.name === 'InvalidParameterException' && error.message?.includes('Auth flow not enabled')) {
        throw new Error('Cognito App Client ayarları hatası: App Client\'ınızda "ADMIN_NO_SRP_AUTH" flow\'u etkin değil. AWS Console\'da App Client ayarlarını güncelleyin. Detaylar için COGNITO_APP_CLIENT_SETUP.md dosyasına bakın.');
      }
      
      // Şifre politikası hatası için özel mesaj
      if (error.name === 'InvalidPasswordException' || error.message?.includes('Password does not conform to policy')) {
        throw new Error('Şifre politikası hatası: Geçici şifre oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
      }
      
      throw error;
    }
  },
};

